class NSSAnchorPills extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute(`nss-sticky-clone`) || this.hasAttribute(`nss-mobile-copy`)) return;

    this.initMobileCopy();
    this.initSticky();
    this.initSpy();
    this.initClicks();
  }

  disconnectedCallback() {
    if (this.sticky) this.sticky.remove();
    if (this.mobileCopy) this.mobileCopy.remove();
    if (this.observer) this.observer.disconnect();
    if (this.spy) this.spy.disconnect();
  }

  initMobileCopy() {
    if (this.mobileCopy) return;
    const heroSection = this.closest(`.shopify-section`);
    if (!heroSection) return;
    const copy = this.cloneNode(true);
    copy.setAttribute(`nss-mobile-copy`, ``);
    copy.removeAttribute(`data-block-id`);
    this.mobileCopy = copy;
    heroSection.insertAdjacentElement(`afterend`, copy);
  }

  initSticky() {
    if (this.sticky) return;

    const clone = this.cloneNode(true);
    clone.setAttribute(`nss-sticky-clone`, ``);
    clone.removeAttribute(`data-block-id`);
    this.sticky = clone;
    document.body.appendChild(clone);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const mobile = window.innerWidth < 990;
          if (entry.target === this && mobile && this.mobileCopy) return;
          if (entry.target === this.mobileCopy && !mobile) return;
          if (entry.boundingClientRect.top <= 0) {
            this.sticky.classList.add(`nss-open`);
          } else {
            this.sticky.classList.remove(`nss-open`);
          }
        });
      },
      { root: null, rootMargin: `0px 0px -100% 0px`, threshold: 0 }
    );
    this.observer.observe(this);
    if (this.mobileCopy) this.observer.observe(this.mobileCopy);
  }

  anchorFor(id) {
    if (!id) return null;
    const matches = document.querySelectorAll(`a[id="${CSS.escape(id)}"]`);
    return matches.length ? matches[matches.length - 1] : null;
  }

  spyTargets() {
    const targets = [];
    this.querySelectorAll(`a[href^="#"]`).forEach((link) => {
      const id = link.getAttribute(`href`).slice(1);
      const anchor = this.anchorFor(id);
      if (!anchor) return;
      const sectionEl = anchor.closest(`.shopify-section`) || anchor.parentElement;
      if (sectionEl) targets.push({ hash: `#` + id, el: sectionEl });
    });
    return targets;
  }

  initSpy() {
    const targets = this.spyTargets();
    if (!targets.length) return;

    this.spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const match = targets.find((t) => t.el === entry.target);
          if (match) this.setActive(match.hash);
        });
      },
      { rootMargin: `-30% 0px -60% 0px`, threshold: 0 }
    );
    targets.forEach((t) => this.spy.observe(t.el));
  }

  initClicks() {
    const onClick = (event) => {
      const link = event.target.closest(`a[href^="#"]`);
      if (!link) return;
      const id = link.getAttribute(`href`).slice(1);
      const anchor = this.anchorFor(id);
      if (!anchor) return;

      event.preventDefault();
      this.setActive(`#` + id);

      const sectionEl = anchor.closest(`.shopify-section`) || anchor.parentElement;
      const top = sectionEl.getBoundingClientRect().top + window.scrollY;
      const down = top > window.scrollY;
      // Anchor CSS offsets depend on --nss-header-group-pos, which changes with
      // scroll direction; compute the destination against the bar that will be
      // visible on landing instead.
      const grp = document.querySelector(`nss-header-group`);
      const ann = document.querySelector(`nss-announcements`);
      const barH = down
        ? this.sticky?.offsetHeight || 0
        : Math.max(0, (grp?.offsetHeight || 0) - (ann?.offsetHeight || 0));
      window.scrollTo({ top: Math.max(0, top - barH - 24), behavior: `smooth` });
      history.replaceState(null, ``, `#` + id);
    };
    this.addEventListener(`click`, onClick);
    if (this.sticky) this.sticky.addEventListener(`click`, onClick);
    if (this.mobileCopy) this.mobileCopy.addEventListener(`click`, onClick);
  }

  setActive(hash) {
    [this, this.sticky, this.mobileCopy].forEach((root) => {
      if (!root) return;
      root.querySelectorAll(`.nss-anchor-pills__pill`).forEach((pill) => {
        pill.classList.toggle(`nss-active`, pill.getAttribute(`href`) === hash);
      });
    });
  }
}

let elementName = `nss-anchor-pills`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSAnchorPills);
}
